import React, { useEffect, useState } from "react";
import { X, LogOut, Monitor, Smartphone, Globe } from "lucide-react";
import { authFetch } from "@/auth/AuthContext";

const uaIcon = (ua = "") => {
  const s = ua.toLowerCase();
  if (s.includes("iphone") || s.includes("android") || s.includes("mobile")) return Smartphone;
  if (s.includes("chrome") || s.includes("safari") || s.includes("firefox") || s.includes("edge")) return Monitor;
  return Globe;
};

const shortUa = (ua = "") => {
  const s = ua || "";
  if (s.length > 80) return s.slice(0, 77) + "…";
  return s || "unknown client";
};

const relTime = (iso) => {
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return d.toLocaleString();
  } catch { return iso; }
};

export default function SessionsModal({ token, distributor, onClose }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    const r = await authFetch(token, `/admin/sessions?user_id=${distributor.id}`);
    setSessions(r.ok ? await r.json() : []);
    setLoading(false);
  }, [token, distributor.id]);

  useEffect(() => { load(); }, [load]);

  const kick = async (jti) => {
    if (!window.confirm("Kick this session? The user will be signed out on their next request.")) return;
    const r = await authFetch(token, `/admin/sessions/${jti}/revoke`, { method: "POST" });
    if (r.ok || r.status === 204) load();
  };

  return (
    <div
      className="fixed inset-0 z-[200] overflow-y-auto bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="sessions-modal"
    >
      <div className="min-h-full flex items-start sm:items-center justify-center px-4 py-10">
        <div
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl"
        >
          <button
            onClick={onClose}
            data-testid="sessions-close"
            className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-black/5 text-black/60"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="p-6 md:p-8">
            <p className="text-[0.65rem] font-bold uppercase tracking-[0.32em] text-[#B02224] mb-2">Active Sessions</p>
            <h2 className="font-editorial italic text-2xl mb-1">{distributor.name}</h2>
            <p className="text-sm text-black/60 mb-6">{distributor.email}</p>

            {loading ? (
              <p className="text-sm text-black/50 py-4">Loading…</p>
            ) : sessions.length === 0 ? (
              <p className="text-sm text-black/50 py-4 border border-dashed border-black/10 rounded-xl px-4 text-center">
                No active sessions — user is currently signed out.
              </p>
            ) : (
              <ul className="space-y-2">
                {sessions.map((s) => {
                  const Icon = uaIcon(s.user_agent);
                  return (
                    <li
                      key={s.jti}
                      className="flex items-center gap-3 rounded-xl border border-black/10 p-3"
                      data-testid={`session-row-${s.jti}`}
                    >
                      <div className="w-9 h-9 rounded-full bg-black/5 text-black/70 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#0F1A12] truncate">{shortUa(s.user_agent)}</p>
                        <p className="text-[0.7rem] text-black/50">
                          {s.ip || "no IP"} · signed in {relTime(s.issued_at)} · expires {relTime(s.expires_at)}
                        </p>
                      </div>
                      <button
                        onClick={() => kick(s.jti)}
                        data-testid={`session-kick-${s.jti}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-black/80 text-white hover:bg-red-700 transition-colors px-3 py-1.5 text-[0.65rem] font-bold uppercase tracking-widest"
                      >
                        <LogOut className="w-3.5 h-3.5" /> Kick
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
